// Custom-column ↔ OpenSearch mapping helpers.
//
// A custom column's UI key is `custom_${crmName}` (see use-*-custom-fields hooks),
// but the value is stored on the OpenSearch doc under `properties.${crmName}` (NO
// `custom_` prefix). Two backend paths must use the un-prefixed, correctly-typed key:
//
//  - gridSummary fill rates: `grid_summary.py` aggregates `properties.<k>.filled`
//    keyed by the raw property name and exact-matches the requested column key, so
//    the frontend must request `properties.<crmName>` — a `custom_`-prefixed key
//    silently yields a 0% footer.
//  - server-side sort: `opensearch_sort` has no `custom_`/`customProperties` branch,
//    so the frontend must send the fully-qualified typed subfield. A date must sort
//    on `value_datetime` (chronological), a number on `value_numeric`; text on the
//    `value_lowercase.keyword` subfield (bare text has fielddata disabled on Furgus).

// The i18next singleton itself, NOT `@/lib/i18n` — same reason as the import below: this module
// must not drag the locale catalogs into every grid suite. `lib/i18n/index.ts` initializes this
// exact instance, and every lookup here happens at call time, so the copy follows a language
// switch without a `t` having to be threaded through the cell renderers.
import i18next from "i18next";

import type { ColumnType } from "@/hooks/useTableColumns";
// Runtime import, but a deliberately light one: `lib/i18n/format` pulls in the
// i18next singleton and pure helpers only — never the locale catalogs — so the
// grid suites that mock heavy modules wholesale are unaffected.
import { activeLanguage, formatNumber } from "@/lib/i18n/format";
// TYPE-only, deliberately. This module is imported by nearly every grid path and by
// suites that `vi.mock("@/lib/graphql/types-and-hooks")` wholesale, so a *runtime* import
// of the generated module would break them. The CustomFieldType values below are
// therefore written as the string literals the enum is defined as.
import type { CustomFieldType } from "@/lib/graphql/types-and-hooks";

const CUSTOM_PREFIX = /^custom_/;

// The valid UI ColumnType values. Any custom field type from the backend must
// resolve to one of these; an unknown/missing type falls back to "text".
const VALID_COLUMN_TYPES = new Set<ColumnType>([
  "ai", "checkbox", "currency", "date", "single-select", "multi-select",
  "email", "location", "number", "phone", "text", "traffic-light", "url",
  // Formula values are computed and materialized by the backend. Leaving this
  // out would resolve FORMULA to "text", which renders an *editable* cell over
  // a value the user must not be able to overwrite.
  "formula",
]);

// Backend CustomFieldType enum values (lowercased) that don't map 1:1 to a UI
// ColumnType by the usual underscore→hyphen swap. STRING is the *default* field
// type, so getting this one wrong breaks the common case.
const BACKEND_TYPE_ALIASES: Record<string, ColumnType> = {
  string: "text",
  datetime: "date",
  ai_search: "ai",
  "ai-search": "ai",
};

/**
 * Normalize a backend `customFieldType` (e.g. "STRING", "AI_SEARCH", "DATETIME",
 * "SINGLE_SELECT") into a valid UI `ColumnType`.
 *
 * The backend enum does not line up with the UI types under a naive
 * lowercase + underscore→hyphen swap: `STRING` → "string" (not "text"),
 * `DATETIME` → "datetime" (not "date"), `AI_SEARCH` → "ai-search" (not "ai").
 * Feeding those raw strings into the grid yields an unknown `ColumnType`, which
 * makes `getColumnWidth` return `undefined` → NaN table width → the column's
 * layout (and drag) breaks. Always route backend types through here.
 *
 * Unknown or missing input falls back to "text".
 */
export function columnTypeFromCustomFieldType(backendType?: string | null): ColumnType {
  const lower = (backendType || "").toLowerCase();
  if (!lower) return "text";
  if (BACKEND_TYPE_ALIASES[lower]) return BACKEND_TYPE_ALIASES[lower];
  const hyphenated = lower.replace(/_/g, "-") as ColumnType;
  return VALID_COLUMN_TYPES.has(hyphenated) ? hyphenated : "text";
}

// The two CustomFieldType values this module branches on, as literals (see the import
// note above). `CustomFieldType.String` / `.Currency`.
const CUSTOM_TYPE_STRING = "STRING";
const CUSTOM_TYPE_CURRENCY = "CURRENCY";

// `CrmIngestionFieldType` (the *ingestion* type — a five-value enum, far smaller than
// CustomFieldType) → CustomFieldType. Lowercased keys so a raw backend string, a
// generated enum member and a hand-written literal all hit the same entry.
const INGESTION_TYPE_TO_CUSTOM_TYPE: Record<string, string> = {
  boolean: "CHECKBOX",
  datetime: "DATETIME",
  number: "NUMBER",
  percentage: "NUMBER",
  string: CUSTOM_TYPE_STRING,
};

// Numeric fields whose crmName means money. The ingestion enum has no CURRENCY member,
// so this is the only way to recover it — the integration-setup flow has always keyed
// off the name this way, and standard fields reuse it so a Deal's Amount is typed the
// same however the field was created. Kept verbatim from that flow: this set is also on
// a WRITE path (it decides the customFieldType new fields are created with), so adding a
// name here retypes fields, which is not this module's call to make.
const CURRENCY_CRM_NAMES = new Set(["amount", "amount_paid", "balance_remaining", "revenue"]);

/**
 * `CrmIngestionFieldType` → `CustomFieldType`, the shared translation used both when
 * CREATING fields from a CRM's schema (use-integration-setup) and when READING standard
 * fields back (`standardFieldsFromCrmIngestion`). One mapper so a field is typed the
 * same on the way in and on the way out.
 */
export function customFieldTypeFromIngestionFieldType(
  fieldType?: string | null,
  crmName?: string | null,
): CustomFieldType {
  const mapped = INGESTION_TYPE_TO_CUSTOM_TYPE[(fieldType || "").toLowerCase()]
    ?? CUSTOM_TYPE_STRING;
  if (mapped === "NUMBER" && crmName && CURRENCY_CRM_NAMES.has(crmName)) {
    return CUSTOM_TYPE_CURRENCY as CustomFieldType;
  }
  return mapped as CustomFieldType;
}

/**
 * The UI `ColumnType` for a STANDARD `crm_ingestion_field` row.
 *
 * Standard fields reach the same destination as custom ones —
 * `columnTypeFromCustomFieldType`, the function the grids type their custom columns
 * with — they just need help getting there, because they routinely arrive with no
 * usable `customFieldType`:
 *
 *   1. If the row declares a real `customFieldType`, use it verbatim. Identical to the
 *      grid's custom-column path.
 *   2. Otherwise it is carrying the `custom_field_type` column DEFAULT 'STRING'
 *      (migration V103), which the standard-field seeder never overwrites — so every
 *      standard row claims to be text. That is what made the formula builder's DATEDIF
 *      picker say "No date fields on this object" for an object that has a Close Date.
 *      Recover the type from the ingestion columns instead and hand off to the same
 *      mapper, so standard and custom fields cannot drift apart.
 *
 * The recovery reads BOTH ingestion columns because the seeder splits the type across
 * them: a CRM date standard field is declared `{"type": STRING, "source_type": DATETIME}`
 * (coffee `ingestion/workflows/workflows/fields.py`), so `crm_field_type` alone reads
 * back as text. The more specific of the two wins.
 *
 * Step 1 can't distinguish a genuinely-STRING field from an unset one — but both mean
 * "text", so nothing is lost.
 */
export function columnTypeForStandardField(field: {
  customFieldType?: string | null;
  crmFieldType?: string | null;
  sourceFieldType?: string | null;
  crmName?: string | null;
}): ColumnType {
  const declared = (field.customFieldType || "").toUpperCase();
  if (declared && declared !== CUSTOM_TYPE_STRING) {
    return columnTypeFromCustomFieldType(declared);
  }
  for (const candidate of [field.sourceFieldType, field.crmFieldType]) {
    const recovered = customFieldTypeFromIngestionFieldType(candidate, field.crmName);
    if (recovered !== CUSTOM_TYPE_STRING) return columnTypeFromCustomFieldType(recovered);
  }
  return "text";
}

/** `custom_website_identified_at_859_coffee` -> `website_identified_at_859_coffee`. */
export function crmNameFromColumnKey(columnKey: string): string {
  return columnKey.replace(CUSTOM_PREFIX, "");
}

// --- Checkbox custom field values -------------------------------------------

/** The value persisted for a *checked* checkbox custom field. Unchecked persists as "". */
export const CHECKBOX_CHECKED_VALUE = "1";

/**
 * Interpret a checkbox custom field's stored value as a boolean.
 *
 * The value can reach the UI in several shapes: a string ("1"/"0"/"true") from
 * `customProperties`, or — because `customProperties`/`propertiesJson` is an
 * untyped JSON scalar carrying `value_int` — a raw number (1/0) or boolean. A
 * strict `value === "1"` check therefore renders a genuinely-checked box as
 * unchecked whenever the value arrives as the number 1. Treat any value in the
 * checked set as checked; everything else ("0", "", null, undefined) is unchecked.
 */
export function isCheckboxChecked(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "number") return value === 1;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes";
}

// --- Date custom field values -----------------------------------------------

/**
 * Parse a stored date custom-field value into a LOCAL `Date`.
 *
 * Stored values are date-only "YYYY-MM-DD" (see `localDateToDateOnly`). Passing
 * that straight to `new Date("2026-07-20")` parses it as UTC midnight, which in
 * any negative-offset timezone (e.g. BRT, UTC-3) is the *previous* calendar day
 * once formatted in local time — the classic off-by-one that made the grid show
 * the day before the one picked. Building the `Date` from the numeric parts lands
 * it on local midnight of the intended day. Falls back to the native parser for
 * legacy full-timestamp values; returns `undefined` for empty/invalid input.
 *
 * Accepts non-string input defensively: the untyped `customProperties` JSON
 * scalar can surface a date value as a raw number, and calling `.trim()` on it
 * threw ("e.trim is not a function"), crashing the grid. Coerce to string first.
 */
export function parseDateOnly(value?: string | number | null): Date | undefined {
  if (value == null) return undefined;
  const str = String(value).trim();
  if (!str) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (match) {
    const [, y, mo, d] = match;
    const date = new Date(Number(y), Number(mo) - 1, Number(d));
    return isNaN(date.getTime()) ? undefined : date;
  }
  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? undefined : fallback;
}

/**
 * Format a `Date` as a date-only "YYYY-MM-DD" string in LOCAL time.
 *
 * The calendar returns a `Date` at local midnight of the picked day. Using
 * `toISOString()` to serialize it shifts to UTC, which drops to the previous day
 * for positive-offset timezones — so serialize from the local Y/M/D parts instead
 * to persist exactly the day the user selected, in any timezone.
 */
export function localDateToDateOnly(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

// --- Display formatting (shared by the grid cell + detail side panel) --------

/**
 * Format a currency custom-field value as "$1,234.5". Strips any non-numeric
 * characters first, so it's idempotent on an already-formatted value. Returns ""
 * for empty/unparseable input. Kept identical to the grid cell so the side panel
 * matches the grid exactly.
 */
export function formatCurrencyValue(v: string | number, symbol: string = "$"): string {
  const num = String(v ?? "").replace(/[^0-9.]/g, "");
  if (!num) return "";
  const parsed = parseFloat(num);
  if (isNaN(parsed)) return "";
  // Grouping and decimal marks follow the reader's language (this used to pin
  // "en-US", so a German user read American numbers inside German UI); the
  // digit count — up to 2 decimals, none forced — is unchanged.
  return `${symbol}${formatNumber(parsed, activeLanguage(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** Format a number custom-field value with thousands separators. "" for empty/unparseable. */
export function formatNumberValue(v: string | number): string {
  const num = String(v ?? "").replace(/[^0-9.-]/g, "");
  if (!num) return "";
  const parsed = parseFloat(num);
  if (isNaN(parsed)) return "";
  return formatNumber(parsed, activeLanguage());
}

/**
 * Turn a multi-select stored value ("B||A") into a human-readable "B, A".
 * Multi-select values are "||"-joined (see CustomColumnCell); a raw display would
 * otherwise show the delimiter.
 */
export function multiSelectToDisplay(v: string): string {
  return String(v ?? "").split("||").filter(Boolean).join(", ");
}

export interface TrafficLightOption {
  value: string;
  label: string;
  /** Tailwind background class for the status dot. */
  color: string;
}

/**
 * Traffic-light choices, shared by the grid cell and the detail panel so both match.
 *
 * `value` is the IDENTIFIER: it is what a cell stores, what the backend round-trips and what
 * `trafficLightOption` matches on, so it stays English and lowercase. Only `labelKey` is copy.
 */
const TRAFFIC_LIGHT_CHOICES: { value: string; labelKey: string; color: string }[] = [
  { value: "green", labelKey: "shared.trafficLight.green", color: "bg-green-500" },
  { value: "yellow", labelKey: "shared.trafficLight.yellow", color: "bg-yellow-500" },
  { value: "red", labelKey: "shared.trafficLight.red", color: "bg-red-500" },
  { value: "", labelKey: "shared.trafficLight.none", color: "bg-muted" },
];

/** Default labels for the three lit values, in the fixed order [green, yellow, red].
 *  Traffic-light custom fields let users override these three labels; the labels are
 *  persisted in the field's `options` array (index 0=green, 1=yellow, 2=red).
 *
 *  THESE ARE PERSISTED VALUES, NOT DISPLAY COPY, which is why they stay English. Both field
 *  editors write them into `options` for any of the three slots the admin leaves blank, and that
 *  array goes to the backend and is then read by every member of the workspace — so translating
 *  this constant would make what gets SAVED depend on the language of whoever created the field.
 *  The translated fallback lives in `TRAFFIC_LIGHT_CHOICES` and applies when `options` carries
 *  nothing for a slot. */
export const TRAFFIC_LIGHT_DEFAULT_LABELS = ["Green", "Yellow", "Red"] as const;

/** Merge user-provided labels (from the field's `options` array, indices 0=green,
 *  1=yellow, 2=red) over the translated defaults. A label the workspace typed is user data and
 *  is never translated; missing/blank entries fall back to the catalog. The "None" entry has no
 *  user-supplied form and always comes from the catalog.
 *
 *  Resolved through the shared i18n instance, at call time rather than at module scope, so the
 *  labels follow a language switch and no caller has to thread a `t` through. */
export function trafficLightOptionsWithLabels(labels?: string[]): TrafficLightOption[] {
  const l = labels ?? [];
  return TRAFFIC_LIGHT_CHOICES.map((choice, i) => {
    const custom = choice.value === "" ? "" : (l[i] ?? "").trim();
    return { value: choice.value, label: custom || i18next.t(choice.labelKey), color: choice.color };
  });
}

/** Resolve a stored traffic-light value to its option, defaulting to "None" (bg-muted). */
export function trafficLightOption(value: unknown, labels?: string[]): TrafficLightOption {
  const v = String(value ?? "");
  const opts = trafficLightOptionsWithLabels(labels);
  return (
    opts.find(o => o.value === v) ??
    opts[opts.length - 1]
  );
}

/**
 * Normalize a URL custom-field value into a safe external href, so a bare
 * "example.com" still links out. Prepends "https://" when no http(s) scheme is
 * present (which also neutralizes non-web schemes like javascript:). Returns ""
 * for empty input.
 */
export function toExternalHref(url: unknown): string {
  const trimmed = String(url ?? "").trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

// --- AI custom field config -------------------------------------------------
//
// An AI (AI_SEARCH) custom field stores its research prompt in the backend
// `fieldConfig` column, a JSONString shaped like:
//   { "ai_prompt_text": "...", "ai_output_instruction_text": "...",
//     "ai_model_provider": "perplexity", "ai_model_name": "sonar-pro" }
// The AI-field Temporal workflow reads these keys back out. Other, unrelated
// keys can also live here (e.g. `meeting_field` from the CRM-sync wizard), so
// reads/writes must preserve keys we don't own.

/**
 * Parse a `fieldConfig` value (a JSON string from the API, an already-parsed
 * object, or null) into a plain object. Returns {} for anything missing or
 * unparseable. The returned object is a fresh copy — safe to mutate/spread.
 */
export function parseFieldConfig(raw: unknown): Record<string, unknown> {
  if (typeof raw === "string" && raw.trim()) {
    try {
      return (JSON.parse(raw) as Record<string, unknown>) ?? {};
    } catch {
      return {};
    }
  }
  if (raw && typeof raw === "object") {
    return { ...(raw as Record<string, unknown>) };
  }
  return {};
}

/** Extract the AI research prompt (`ai_prompt_text`) from a `fieldConfig`, or "" if absent. */
export function aiPromptFromFieldConfig(raw: unknown): string {
  const value = parseFieldConfig(raw).ai_prompt_text;
  return typeof value === "string" ? value : "";
}

/**
 * Merge an AI research `prompt` (and optional output-format instructions) into an
 * existing `fieldConfig`, preserving every other key, and return it as the
 * JSONString the API expects. Use on UPDATE so a prompt edit doesn't wipe model
 * settings or a `meeting_field` marker. `outputInstructionText` is only written
 * when non-empty (the UI doesn't yet capture it, so we leave any existing value
 * untouched when it's absent).
 */
export function withAiPrompt(
  existing: unknown,
  prompt: string,
  outputInstructionText?: string,
): string {
  const base = parseFieldConfig(existing);
  const next: Record<string, unknown> = { ...base, ai_prompt_text: prompt.trim() };
  const instruction = outputInstructionText?.trim();
  if (instruction) next.ai_output_instruction_text = instruction;
  return JSON.stringify(next);
}

// --- Formula custom fields ---------------------------------------------------

/** Extract the formula expression (`formula_expression`) from a `fieldConfig`. */
export function formulaFromFieldConfig(raw: unknown): string {
  const value = parseFieldConfig(raw).formula_expression;
  return typeof value === "string" ? value : "";
}

/**
 * Extract what the formula evaluates to (`formula_result_type`). Drives the sort
 * subfield; anything unrecognized sorts as text.
 */
export function formulaResultTypeFromFieldConfig(raw: unknown): string {
  const value = parseFieldConfig(raw).formula_result_type;
  return typeof value === "string" ? value : "";
}

/**
 * Merge a formula `expression` (and what it evaluates to) into an existing
 * `fieldConfig`, preserving every other key, and return it as the JSONString the
 * API expects. Use on UPDATE so editing the expression doesn't wipe unrelated
 * config. Editing the expression makes the backend re-evaluate the field.
 */
export function withFormula(
  existing: unknown,
  expression: string,
  resultType?: string,
): string {
  const base = parseFieldConfig(existing);
  const next: Record<string, unknown> = {
    ...base,
    formula_expression: expression.trim(),
    formula_version: 1,
  };
  if (resultType?.trim()) next.formula_result_type = resultType.trim();
  return JSON.stringify(next);
}

/** Fill-rate (gridSummary) key for a custom column: `properties.<crmName>`. */
export function customColumnFillRateKey(columnKey: string): string {
  return `properties.${crmNameFromColumnKey(columnKey)}`;
}

/**
 * Server-side sort field for a custom column, chosen by field type so the sort is
 * semantically correct (dates chronological, numbers numeric, text collated).
 * `type` is the hyphenated UI type from the custom-fields hook (e.g. "date",
 * "number", "single-select"); undefined/unknown falls back to text.
 *
 * A formula column's stored value can be a number, a date or text depending on
 * the expression, so its sort field comes from the `resultType` the builder
 * stamped into `fieldConfig` — sorting every formula as text would order "10"
 * before "9".
 */
export function customColumnSortField(columnKey: string, type?: string, resultType?: string): string {
  const crmName = crmNameFromColumnKey(columnKey);
  const t = (type || "").toLowerCase() === "formula"
    ? (resultType || "").toLowerCase()
    : (type || "").toLowerCase();
  if (t.includes("date")) return `properties.${crmName}.value_datetime`;
  if (t === "number" || t === "currency" || t === "percent" || t === "percentage") {
    return `properties.${crmName}.value_numeric`;
  }
  return `properties.${crmName}.value_lowercase.keyword`;
}
